import { collection, addDoc, query, where, orderBy, getDocs, doc, getDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../lib/firebase';
import { Post, Comment } from '../types/post';

interface CreatePostInput {
  title: string;
  content: string;
  subtitle?: string;
  excerpt?: string;
  coverImage?: string;
  tags: string[];
  author: {
    id: string;
    name: string;
    avatar?: string;
    bio?: string;
  };
  published?: boolean;
  featured?: boolean;
}

export interface Notification {
  id: string;
  type: 'comment' | 'reply' | 'reaction' | 'mention';
  postId: string;
  commentId?: string;
  actorName: string;
  userId: string; // ID of the user who should receive the notification
  createdAt: string;
  read: boolean;
}

class PostService {
  private static instance: PostService;
  private readonly COLLECTION_NAME = 'posts';
  private listeners: Map<string, (posts: Post[]) => void> = new Map();
  private notificationListeners: Map<string, (notification: Notification) => void> = new Map();

  private constructor() {}

  public static getInstance(): PostService {
    if (!PostService.instance) {
      PostService.instance = new PostService();
    }
    return PostService.instance;
  }

  async getPost(postId: string): Promise<Post | null> {
    try {
      const postRef = doc(db, this.COLLECTION_NAME, postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) return null;
      
      return {
        id: postDoc.id,
        ...postDoc.data()
      } as Post;
    } catch (error) {
      console.error('Error fetching post:', error);
      return null;
    }
  }

  async deletePost(postId: string, userId: string): Promise<void> {
    try {
      const postRef = doc(db, this.COLLECTION_NAME, postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) {
        throw new Error('Post not found');
      }
      
      const post = postDoc.data() as Post;
      if (post.author.id !== userId) {
        throw new Error('Not authorized to delete this post');
      }
      
      await deleteDoc(postRef);
      
      // Notify listeners about the deletion
      this.listeners.forEach((callback) => {
        callback([]); // Trigger a refresh
      });
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  }

  async likePost(postId: string, userId: string): Promise<void> {
    try {
      const postRef = doc(db, this.COLLECTION_NAME, postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) throw new Error('Post not found');
      
      const post = postDoc.data();
      const likes = post.likedBy || [];
      
      if (!likes.includes(userId)) {
        await updateDoc(postRef, {
          likedBy: [...likes, userId]
        });
      }
    } catch (error) {
      console.error('Error liking post:', error);
      throw error;
    }
  }

  async unlikePost(postId: string, userId: string): Promise<void> {
    try {
      const postRef = doc(db, this.COLLECTION_NAME, postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) throw new Error('Post not found');
      
      const post = postDoc.data();
      const likes = post.likedBy || [];
      
      await updateDoc(postRef, {
        likedBy: likes.filter((id: string) => id !== userId)
      });
    } catch (error) {
      console.error('Error unliking post:', error);
      throw error;
    }
  }

  async bookmarkPost(postId: string, userId: string): Promise<void> {
    try {
      const postRef = doc(db, this.COLLECTION_NAME, postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) throw new Error('Post not found');
      
      const post = postDoc.data();
      const bookmarks = post.bookmarkedBy || [];
      
      if (!bookmarks.includes(userId)) {
        await updateDoc(postRef, {
          bookmarkedBy: [...bookmarks, userId]
        });
      }
    } catch (error) {
      console.error('Error bookmarking post:', error);
      throw error;
    }
  }

  async unbookmarkPost(postId: string, userId: string): Promise<void> {
    try {
      const postRef = doc(db, this.COLLECTION_NAME, postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) throw new Error('Post not found');
      
      const post = postDoc.data();
      const bookmarks = post.bookmarkedBy || [];
      
      await updateDoc(postRef, {
        bookmarkedBy: bookmarks.filter((id: string) => id !== userId)
      });
    } catch (error) {
      console.error('Error removing bookmark:', error);
      throw error;
    }
  }

  async updatePost(postId: string, data: Partial<CreatePostInput>): Promise<void> {
    try {
      const postRef = doc(db, this.COLLECTION_NAME, postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) throw new Error('Post not found');
      
      const currentPost = postDoc.data();
      await updateDoc(postRef, {
        ...data,
        updatedAt: new Date().toISOString(),
        slug: data.title ? data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') : currentPost.slug
      });
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  }

  listenToSavedPosts(userId: string, callback: (posts: Post[]) => void): () => void {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('bookmarkedBy', 'array-contains', userId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
      callback(posts);
    }, (error) => {
      console.error('Error listening to saved posts:', error);
    });

    return unsubscribe;
  }

  async getSavedPosts(userId: string): Promise<Post[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('bookmarkedBy', 'array-contains', userId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
    } catch (error) {
      console.error('Error fetching saved posts:', error);
      return [];
    }
  }

  // Renamed to avoid duplicate function implementation
  async fetchPostsByUsername(username: string): Promise<Post[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('author.username', '==', username)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
    } catch (error) {
      console.error('Error fetching posts by username:', error);
      return [];
    }
  }

  subscribeToUpdates(key: string, callback: (posts: Post[]) => void) {
    this.listeners.set(key, callback);
    
    // Set up real-time listener
    const q = query(
      collection(db, 'posts'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
      
      callback(posts);
    });

    return () => {
      unsubscribe();
      this.listeners.delete(key);
    };
  }

  subscribeToNotifications(userId: string, callback: (notification: Notification) => void) {
    const key = `notifications_${userId}`;
    this.notificationListeners.set(key, callback);
    
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          callback(change.doc.data() as Notification);
        }
      });
    });

    return () => {
      unsubscribe();
      this.notificationListeners.delete(key);
    };
  }

  private async createNotification(notification: Omit<Notification, 'id' | 'createdAt' | 'read'>): Promise<void> {
    const notificationData = {
      ...notification,
      id: uuidv4(),
      createdAt: new Date().toISOString(),
      read: false
    };

    await addDoc(collection(db, 'notifications'), notificationData);
  }

  async addComment(postId: string, content: string, user: { uid: string; displayName?: string | null; photoURL?: string }, parentId?: string): Promise<void> {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) return;
    
    const post = postDoc.data() as Post;
    const comments = post.comments || [];
    
    const newComment: Comment = {
      id: uuidv4(),
      content,
      authorId: user.uid,
      authorName: user.displayName || 'Anonymous',
      authorAvatar: user.photoURL || undefined,
      createdAt: new Date().toISOString(),
      likes: []
    };
    
    await updateDoc(postRef, {
      comments: parentId
        ? comments.map(c => 
            c.id === parentId
              ? { ...c /* replies property doesn't exist on Comment */ }
              : c
          )
        : [...comments, newComment]
    });
  }

  async toggleCommentReaction(postId: string, commentId: string, user: { uid: string; displayName?: string | null }, _emoji: string): Promise<void> {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) return;
    
    const post = postDoc.data() as Post;
    const comments = post.comments || [];
    
    const updatedComments = comments.map(comment => {
      if (comment.id === commentId) {
        // Use likes array instead of reactions which doesn't exist on Comment type
        const likes = comment.likes || [];
        const existingLikeIndex = likes.indexOf(user.uid);
        
        if (existingLikeIndex > -1) {
          likes.splice(existingLikeIndex, 1);
        } else {
          likes.push(user.uid);

          // Create notification for reaction
          this.createNotification({
            type: 'reaction',
            postId,
            commentId,
            actorName: user.displayName || 'Anonymous',
            userId: comment.authorId
          });
        }
        
        return { ...comment, likes };
      }
      return comment;
    });
    
    await updateDoc(postRef, { comments: updatedComments });
  }

  async deleteComment(postId: string, commentId: string, userId: string): Promise<void> {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) return;
    
    const post = postDoc.data() as Post;
    const comments = post.comments || [];
    
    // Only allow deletion if user is the comment author
    const filteredComments = comments.filter(
      comment => !(comment.id === commentId && comment.authorId === userId)
    );
    
    if (filteredComments.length !== comments.length) {
      await updateDoc(postRef, { comments: filteredComments });
    }
  }

  async createPost(input: { authorId: string; content: string; authorName: string; authorAvatar?: string }): Promise<Post> {
    try {
      const slug = this.generateSlug(input.authorName);
      const readingTime = this.calculateReadingTime(input.content);
      
      const postData: Omit<Post, 'id'> = {
        title: 'New Post',
        content: input.content,
        excerpt: input.content.substring(0, 160) + '...',
        slug,
        author: {
          id: input.authorId,
          name: input.authorName,
          username: slug,
          avatar: input.authorAvatar,
          bio: ''
        },
        tags: [],
        readingTime,
        likedBy: [],
        bookmarkedBy: [],
        comments: [],
        published: true,
        featured: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), postData);
      return {
        id: docRef.id,
        ...postData,
      };
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  async getPostsByUsername(username: string): Promise<Post[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('published', '==', true),
        where('author.username', '==', username)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
    } catch (error) {
      console.error('Error getting posts by username:', error);
      return [];
    }
  }

  async getFeed(options: { limit?: number; featured?: boolean; tag?: string } = {}): Promise<Post[]> {
    try {
      // First get published posts without complex queries
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('published', '==', true)
      );
      
      const snapshot = await getDocs(q);
      let posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
      
      // Apply filters in memory
      if (options.featured) {
        posts = posts.filter(post => post.featured === true);
      }
      
      if (options.tag) {
        posts = posts.filter(post => 
          post.tags?.includes(options.tag!)
        );
      }
      
      // Sort by createdAt
      posts.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });
      
      // Apply limit
      return posts.slice(0, options.limit || 20);
    } catch (error) {
      console.error('Error getting feed:', error);
      return []; // Return empty array instead of throwing
    }
  }

  async toggleLike(postId: string, userId: string): Promise<void> {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) return;
    
    const post = postDoc.data() as Post;
    const likedBy = post.likedBy || [];
    
    if (likedBy.includes(userId)) {
      await updateDoc(postRef, {
        likedBy: likedBy.filter((id: string) => id !== userId)
      });
    } else {
      await updateDoc(postRef, {
        likedBy: [...likedBy, userId]
      });
    }
  }

  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  }

  private calculateReadingTime(content: string): number {
    const wordsPerMinute = 200;
    const words = content.trim().split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  }

  async getTrendingTopics(): Promise<Array<{topic: string, count: number}>> {
    try {
      // Get published posts without complex queries
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('published', '==', true)
      );

      const snapshot = await getDocs(q);
      const topicCounts = new Map<string, number>();
      const now = new Date().getTime();
      const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000; // 7 days ago

      snapshot.docs.forEach(doc => {
        const post = doc.data() as Post;
        const postDate = new Date(post.createdAt).getTime();
        
        // Only count posts from the last week
        if (postDate > oneWeekAgo) {
          post.tags?.forEach(tag => {
            topicCounts.set(tag, (topicCounts.get(tag) || 0) + 1);
          });
        }
      });

      return Array.from(topicCounts.entries())
        .map(([topic, count]) => ({ topic, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    } catch (error) {
      console.error('Error getting trending topics:', error);
      return [];
    }
  }
}

export default PostService;
